import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Progress,
  Table,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import { useGetUserDetailsQuery } from "store/features/users/userManagementApi";

const roleFormatMap = {
  admin: { color: "danger", text: "Admin" },
  super_admin: { color: "dark", text: "Super Admin" },
  project_manager: { color: "warning", text: "Project Manager" },
  developer: { color: "primary", text: "Developer" },
  designer: { color: "info", text: "Designer" },
  marketing: { color: "success", text: "Marketing" },
  hr: { color: "purple", text: "HR" },
  business_operation: { color: "info", text: "Business Operation" },
  client: { color: "secondary", text: "Client" },
};

const getRoleConfig = role => {
  if (!role) return { color: "secondary", text: "N/A" };
  const clean = String(role).toLowerCase().trim().replace(/[-\s]/g, "_");
  if (roleFormatMap[clean]) return roleFormatMap[clean];
  const formatted = clean
    .split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return { color: "primary", text: formatted };
};

const QwikITUserDetailsModal = ({ isOpen, toggle, user, onEditUser }) => {
  const [activeTab, setActiveTab] = useState("1");
  const [zoomImage, setZoomImage] = useState(null);

  const userId = user?.id;
  const { data, isLoading, isError, refetch } = useGetUserDetailsQuery(userId, {
    skip: !isOpen || !userId,
  });

  const userData = data?.user || user || {};
  const empProfile = data?.employee_profile;
  const assignedProjects = data?.assigned_projects || [];
  const tasks = data?.tasks || [];
  const targets = data?.targets || [];
  const payrollHistory = data?.payroll_history || [];

  const roleInfo = getRoleConfig(userData.role);

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const calculateAge = dob => {
    if (!dob) return "N/A";
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return "N/A";
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age > 0 ? `${age} years` : "N/A";
  };

  const getStatusBadge = status => {
    const lower = (status || "").toLowerCase();
    if (lower === "completed" || lower === "active" || lower === "paid") {
      return <Badge color="success">{status}</Badge>;
    }
    if (lower === "in progress" || lower === "in_progress" || lower === "pending") {
      return <Badge color="warning">{status}</Badge>;
    }
    if (lower === "absent" || lower === "failed" || lower === "cancelled" || lower === "disabled") {
      return <Badge color="danger">{status}</Badge>;
    }
    return <Badge color="primary">{status || "N/A"}</Badge>;
  };

  const parsePermissions = perms => {
    if (!perms) return [];
    if (Array.isArray(perms)) return perms;
    if (typeof perms === "string") {
      try {
        const parsed = JSON.parse(perms);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        return perms.split(",").map(p => p.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const crmLabels = {
    qwik_it: { label: "Softzen IT", color: "primary" },
    qwik_medic: { label: "Qwik Medic", color: "success" },
    qwik_bistro: { label: "Qwik Bistro", color: "warning" },
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl" centered scrollable>
        {/* Custom Header with Profile Cover */}
        <ModalHeader toggle={toggle} className="border-bottom-0 pb-0">
          <div className="d-flex align-items-center justify-content-between w-100 pe-3">
            <span className="fs-5 fw-bold text-dark">
              <i className="fas fa-id-card me-2 text-primary"></i>User Profile & Full Details
            </span>
          </div>
        </ModalHeader>

        <ModalBody className="p-0">
          {/* Top Hero Banner */}
          <div
            className="p-4"
            style={{
              background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
              color: "#fff",
            }}
          >
            <Row className="align-items-center">
              <Col xs="auto">
                <div className="position-relative">
                  {userData.image ? (
                    <img
                      src={userData.image}
                      alt={userData.nickname || "User"}
                      className="rounded-circle img-thumbnail shadow-sm"
                      style={{ width: "95px", height: "95px", objectFit: "cover", border: "3px solid #fff" }}
                    />
                  ) : (
                    <div
                      className="rounded-circle bg-white text-primary d-flex align-items-center justify-content-center shadow-sm"
                      style={{ width: "95px", height: "95px", fontSize: "40px" }}
                    >
                      <i className="fas fa-user"></i>
                    </div>
                  )}
                  <span
                    className={`position-absolute bottom-0 end-0 p-2 border border-light rounded-circle ${
                      userData.account_status ? "bg-success" : "bg-danger"
                    }`}
                    title={userData.account_status ? "Active Account" : "Disabled Account"}
                  ></span>
                </div>
              </Col>
              <Col>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                  <h4 className="text-white mb-0 fw-bold">{userData.nickname || "N/A"}</h4>
                  <Badge color={roleInfo.color} className="p-2 fs-7 text-uppercase fw-bold">
                    <i className="fas fa-user-shield me-1"></i> {roleInfo.text}
                  </Badge>
                  <Badge color={userData.account_status ? "success" : "danger"} className="p-2">
                    {userData.account_status ? "Active" : "Disabled"}
                  </Badge>
                </div>
                <p className="text-white-50 mb-2 fs-6">
                  {userData.position || empProfile?.designation || roleInfo.text}{" "}
                  {empProfile?.department ? `• ${empProfile.department}` : ""}
                </p>
                <div className="d-flex flex-wrap gap-3 text-white-50 fs-7">
                  {userData.emp_id && (
                    <span>
                      <i className="fas fa-hashtag me-1 text-warning"></i>Emp ID: <strong className="text-white">{userData.emp_id}</strong>
                    </span>
                  )}
                  {userData.biometric_user_id && (
                    <span>
                      <i className="fas fa-fingerprint me-1 text-info"></i>Bio ID: <strong className="text-white">{userData.biometric_user_id}</strong>
                    </span>
                  )}
                  {userData.official_email && (
                    <span>
                      <i className="fas fa-envelope me-1 text-light"></i>{userData.official_email}
                    </span>
                  )}
                  {userData.phone && (
                    <span>
                      <i className="fas fa-phone me-1 text-success"></i>{userData.phone}
                    </span>
                  )}
                </div>
              </Col>
              <Col xs="auto" className="d-none d-md-block text-end">
                {onEditUser && (
                  <Button
                    color="light"
                    size="sm"
                    className="fw-semibold me-2"
                    onClick={() => {
                      toggle();
                      onEditUser(userData);
                    }}
                  >
                    <i className="fas fa-edit me-1 text-primary"></i> Edit User
                  </Button>
                )}
                <Button color="outline-light" size="sm" onClick={() => refetch()} title="Refresh Details">
                  <i className="fas fa-sync-alt"></i>
                </Button>
              </Col>
            </Row>
          </div>

          {/* Quick Metrics Bar */}
          <div className="bg-light px-4 py-2 border-bottom shadow-sm">
            <Row className="text-center g-2">
              <Col xs="6" sm="3">
                <div className="p-2">
                  <small className="text-muted text-uppercase fw-semibold d-block">Monthly Salary</small>
                  <span className="fs-5 fw-bold text-success">
                    ৳ {(empProfile?.salary ? Number(empProfile.salary) : 0).toLocaleString()}
                  </span>
                </div>
              </Col>
              <Col xs="6" sm="3">
                <div className="p-2">
                  <small className="text-muted text-uppercase fw-semibold d-block">Assigned Projects</small>
                  <span className="fs-5 fw-bold text-primary">{assignedProjects.length}</span>
                </div>
              </Col>
              <Col xs="6" sm="3">
                <div className="p-2">
                  <small className="text-muted text-uppercase fw-semibold d-block">Assigned Tasks</small>
                  <span className="fs-5 fw-bold text-info">{tasks.length}</span>
                </div>
              </Col>
              <Col xs="6" sm="3">
                <div className="p-2">
                  <small className="text-muted text-uppercase fw-semibold d-block">Joining Date</small>
                  <span className="fs-6 fw-bold text-dark">
                    {formatDate(userData.joining_date || empProfile?.hire_date)}
                  </span>
                </div>
              </Col>
            </Row>
          </div>

          {/* Navigation Tabs */}
          <div className="px-4 pt-3">
            <Nav tabs className="nav-tabs-custom">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "1" })}
                  onClick={() => toggleTab("1")}
                  style={{ cursor: "pointer", fontWeight: "600" }}
                >
                  <i className="fas fa-user-circle me-2 text-primary"></i>Overview & Personal
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "2" })}
                  onClick={() => toggleTab("2")}
                  style={{ cursor: "pointer", fontWeight: "600" }}
                >
                  <i className="fas fa-project-diagram me-2 text-warning"></i>
                  Projects & Tasks{" "}
                  {assignedProjects.length > 0 && (
                    <Badge color="primary" pill className="ms-1">
                      {assignedProjects.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "3" })}
                  onClick={() => toggleTab("3")}
                  style={{ cursor: "pointer", fontWeight: "600" }}
                >
                  <i className="fas fa-money-bill-wave me-2 text-success"></i>Salary & Payroll
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "4" })}
                  onClick={() => toggleTab("4")}
                  style={{ cursor: "pointer", fontWeight: "600" }}
                >
                  <i className="fas fa-file-alt me-2 text-danger"></i>Documents & Verification
                </NavLink>
              </NavItem>
            </Nav>
          </div>

          {/* Loading Indicator */}
          {isLoading && (
            <div className="text-center py-5">
              <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
              <p className="mt-2 text-muted fw-semibold">Loading user full details...</p>
            </div>
          )}

          {/* Tabs Content */}
          {!isLoading && (
            <TabContent activeTab={activeTab} className="p-4">
              {/* TAB 1: OVERVIEW & PERSONAL */}
              <TabPane tabId="1">
                <Row className="g-3">
                  <Col md="6">
                    <Card className="border shadow-none h-100">
                      <CardBody>
                        <h6 className="card-title text-primary border-bottom pb-2 fw-bold">
                          <i className="fas fa-address-book me-2"></i>Contact Information
                        </h6>
                        <Table responsive borderless className="mb-0 fs-7">
                          <tbody>
                            <tr>
                              <th style={{ width: "40%" }} className="text-muted">Official Email:</th>
                              <td><strong>{userData.official_email || "N/A"}</strong></td>
                            </tr>
                            <tr>
                              <th className="text-muted">Personal Email:</th>
                              <td>{userData.personal_email || "N/A"}</td>
                            </tr>
                            <tr>
                              <th className="text-muted">Primary Phone:</th>
                              <td><strong>{userData.phone || "N/A"}</strong></td>
                            </tr>
                            <tr>
                              <th className="text-muted">Alternate Phone:</th>
                              <td>{userData.alt_phone || "N/A"}</td>
                            </tr>
                            <tr>
                              <th className="text-muted">Emergency Phone:</th>
                              <td>
                                <span className="text-danger fw-semibold">
                                  {userData.emergency_phone || "N/A"}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <th className="text-muted">City / Region:</th>
                              <td>{userData.city || "N/A"}</td>
                            </tr>
                            <tr>
                              <th className="text-muted">Address:</th>
                              <td>{userData.address || "N/A"}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md="6">
                    <Card className="border shadow-none h-100">
                      <CardBody>
                        <h6 className="card-title text-primary border-bottom pb-2 fw-bold">
                          <i className="fas fa-user-tag me-2"></i>Personal & Employment Info
                        </h6>
                        <Table responsive borderless className="mb-0 fs-7">
                          <tbody>
                            <tr>
                              <th style={{ width: "40%" }} className="text-muted">User Role:</th>
                              <td>
                                <Badge color={roleInfo.color} className="p-2 fs-7 text-uppercase">
                                  {roleInfo.text}
                                </Badge>
                              </td>
                            </tr>
                            <tr>
                              <th className="text-muted">Position / Designation:</th>
                              <td><strong>{userData.position || empProfile?.designation || "N/A"}</strong></td>
                            </tr>
                            <tr>
                              <th className="text-muted">Department:</th>
                              <td>{empProfile?.department || "General"}</td>
                            </tr>
                            <tr>
                              <th className="text-muted">Date of Birth:</th>
                              <td>{formatDate(userData.date_of_birth)} ({calculateAge(userData.date_of_birth)})</td>
                            </tr>
                            <tr>
                              <th className="text-muted">Gender:</th>
                              <td className="text-capitalize">{userData.gender || "N/A"}</td>
                            </tr>
                            <tr>
                              <th className="text-muted">Blood Group:</th>
                              <td>
                                {userData.blood_group ? (
                                  <Badge color="danger" pill className="px-2 py-1">
                                    {userData.blood_group}
                                  </Badge>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th className="text-muted">Experience:</th>
                              <td>{userData.experience_years ? `${userData.experience_years} Years` : "N/A"}</td>
                            </tr>
                            <tr>
                              <th className="text-muted">Joining Date:</th>
                              <td>{formatDate(userData.joining_date || empProfile?.hire_date)}</td>
                            </tr>
                            <tr>
                              <th className="text-muted">Leaving Date:</th>
                              <td>{userData.leaving_date ? formatDate(userData.leaving_date) : "Currently Employed"}</td>
                            </tr>
                            <tr>
                              <th className="text-muted">Associated Team:</th>
                              <td>{userData.associated_team_name || "General Team"}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* CRM Permissions & System info */}
                  <Col xs="12">
                    <Card className="border shadow-none">
                      <CardBody>
                        <h6 className="card-title text-primary border-bottom pb-2 fw-bold">
                          <i className="fas fa-shield-alt me-2"></i>CRM Access & Permissions
                        </h6>
                        <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                          <span className="fw-semibold text-muted me-2">Granted CRM Portals:</span>
                          {parsePermissions(userData.crm_permissions).length > 0 ? (
                            parsePermissions(userData.crm_permissions).map((crm, idx) => {
                              const conf = crmLabels[crm] || { label: crm, color: "secondary" };
                              return (
                                <Badge key={idx} color={conf.color} className="p-2 fs-7">
                                  <i className="fas fa-check-circle me-1"></i> {conf.label}
                                </Badge>
                              );
                            })
                          ) : (
                            <span className="text-muted">No specific CRM permissions assigned</span>
                          )}
                          {userData.default_crm && (
                            <span className="ms-3 text-muted">
                              Default Portal: <strong className="text-dark">{userData.default_crm}</strong>
                            </span>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              {/* TAB 2: ASSIGNED PROJECTS & TASKS */}
              <TabPane tabId="2">
                <h6 className="fw-bold text-dark mb-3">
                  <i className="fas fa-cubes me-2 text-warning"></i>Assigned Projects ({assignedProjects.length})
                </h6>

                {assignedProjects.length === 0 ? (
                  <div className="alert alert-info text-center py-4">
                    <i className="fas fa-info-circle fa-2x mb-2 d-block"></i>
                    No projects are currently assigned to this user.
                  </div>
                ) : (
                  <Row className="g-3 mb-4">
                    {assignedProjects.map((proj, index) => (
                      <Col md="6" key={index}>
                        <Card className="border shadow-none h-100">
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 className="fw-bold mb-1 text-primary">{proj.name}</h6>
                                <small className="text-muted">
                                  Client: <strong className="text-dark">{proj.client_name}</strong>
                                </small>
                              </div>
                              {getStatusBadge(proj.status)}
                            </div>

                            <div className="mb-2">
                              <span className="text-muted fs-7 me-2">Role in Project:</span>
                              <div className="d-inline-flex flex-wrap gap-1">
                                {(proj.roles || []).map((r, rIdx) => (
                                  <Badge key={rIdx} color="soft-primary" className="bg-primary text-white">
                                    {r}
                                  </Badge>
                                ))}
                              </div>
                            </div>

                            <div className="mb-3">
                              <div className="d-flex justify-content-between fs-7 mb-1">
                                <span>Progress:</span>
                                <strong>{proj.progress || 0}%</strong>
                              </div>
                              <Progress value={proj.progress || 0} color="primary" style={{ height: "6px" }} />
                            </div>

                            <Row className="fs-7 text-muted border-top pt-2 mt-2">
                              <Col xs="6">
                                <i className="fas fa-calendar-alt me-1"></i>Start: {formatDate(proj.start_date)}
                              </Col>
                              <Col xs="6" className="text-end">
                                <i className="fas fa-calendar-check me-1"></i>End: {formatDate(proj.end_date)}
                              </Col>
                              {proj.budget > 0 && (
                                <Col xs="12" className="mt-1 text-success fw-semibold">
                                  <i className="fas fa-tag me-1"></i>Budget: ৳ {Number(proj.budget).toLocaleString()}
                                </Col>
                              )}
                            </Row>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}

                {/* Tasks Section */}
                <h6 className="fw-bold text-dark mb-3 mt-4">
                  <i className="fas fa-tasks me-2 text-info"></i>Assigned Tasks ({tasks.length})
                </h6>
                {tasks.length === 0 ? (
                  <p className="text-muted fs-7">No specific tasks recorded for this user.</p>
                ) : (
                  <Table responsive hover className="align-middle fs-7 table-nowrap border">
                    <thead className="table-light">
                      <tr>
                        <th>Task Title</th>
                        <th>Project</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Due Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task, idx) => (
                        <tr key={idx}>
                          <td className="fw-semibold text-dark">{task.title}</td>
                          <td>{task.project_name}</td>
                          <td>
                            <Badge
                              color={
                                task.priority === "High"
                                  ? "danger"
                                  : task.priority === "Medium"
                                  ? "warning"
                                  : "info"
                              }
                            >
                              {task.priority || "Normal"}
                            </Badge>
                          </td>
                          <td>{getStatusBadge(task.status)}</td>
                          <td>{formatDate(task.due_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </TabPane>

              {/* TAB 3: SALARY & PAYROLL */}
              <TabPane tabId="3">
                <Row className="g-3 mb-4">
                  <Col md="4">
                    <Card className="border bg-soft-success shadow-none h-100" style={{ backgroundColor: "#e8f5e9" }}>
                      <CardBody>
                        <small className="text-uppercase text-muted fw-bold">Monthly Base Salary</small>
                        <h3 className="text-success fw-bold mt-2 mb-0">
                          ৳ {(empProfile?.salary ? Number(empProfile.salary) : 0).toLocaleString()}
                        </h3>
                        <p className="text-muted fs-7 mt-2 mb-0">
                          Status: <strong className="text-dark">{empProfile?.work_status || "Active"}</strong>
                        </p>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="4">
                    <Card className="border shadow-none h-100">
                      <CardBody>
                        <small className="text-uppercase text-muted fw-bold">Department & Role</small>
                        <h5 className="text-dark fw-bold mt-2 mb-1">
                          {empProfile?.department || userData.position || "General Staff"}
                        </h5>
                        <p className="text-muted fs-7 mb-0">
                          Designation: <strong>{empProfile?.designation || userData.position || "N/A"}</strong>
                        </p>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="4">
                    <Card className="border shadow-none h-100">
                      <CardBody>
                        <small className="text-uppercase text-muted fw-bold">Performance Score</small>
                        <h4 className="text-primary fw-bold mt-2 mb-1">
                          {empProfile?.performance_score || 0} / 100
                        </h4>
                        <p className="text-muted fs-7 mb-0">
                          Hire Date: <strong>{formatDate(empProfile?.hire_date || userData.joining_date)}</strong>
                        </p>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Targets (if available) */}
                {targets.length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold text-dark mb-2">
                      <i className="fas fa-bullseye me-2 text-danger"></i>Sales / Monthly Targets
                    </h6>
                    <Table responsive hover className="align-middle fs-7 table-nowrap border">
                      <thead className="table-light">
                        <tr>
                          <th>Month</th>
                          <th>Target Amount</th>
                          <th>Commission Rate</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {targets.map((tgt, idx) => (
                          <tr key={idx}>
                            <td className="fw-bold">{tgt.month}</td>
                            <td className="text-success fw-bold">৳ {Number(tgt.target_amount).toLocaleString()}</td>
                            <td>{tgt.commission_rate}%</td>
                            <td className="text-muted">{tgt.notes || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Payroll Records History */}
                <h6 className="fw-bold text-dark mb-2">
                  <i className="fas fa-receipt me-2 text-success"></i>Monthly Payroll Records
                </h6>
                {payrollHistory.length === 0 ? (
                  <div className="alert alert-light border text-center py-3">
                    <p className="mb-0 text-muted fs-7">No generated payroll records found yet for this user.</p>
                  </div>
                ) : (
                  <Table responsive hover className="align-middle fs-7 table-nowrap border">
                    <thead className="table-light">
                      <tr>
                        <th>Month</th>
                        <th>Base Salary</th>
                        <th>Present/Days</th>
                        <th>Overtime</th>
                        <th>Allowances</th>
                        <th>Deductions</th>
                        <th>Net Salary</th>
                        <th>Status</th>
                        <th>Payment Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrollHistory.map((pr, idx) => (
                        <tr key={idx}>
                          <td className="fw-bold text-primary">{pr.month}</td>
                          <td>৳ {Number(pr.base_salary).toLocaleString()}</td>
                          <td>{pr.present_days} / {pr.working_days}</td>
                          <td>৳ {Number(pr.overtime_pay || 0).toLocaleString()}</td>
                          <td className="text-success">+৳ {Number(pr.allowances || 0).toLocaleString()}</td>
                          <td className="text-danger">-৳ {Number(pr.deductions || 0).toLocaleString()}</td>
                          <td className="fw-bold text-dark">৳ {Number(pr.net_salary).toLocaleString()}</td>
                          <td>{getStatusBadge(pr.payment_status)}</td>
                          <td>{formatDate(pr.payment_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </TabPane>

              {/* TAB 4: DOCUMENTS & VERIFICATION */}
              <TabPane tabId="4">
                <h6 className="fw-bold text-dark mb-3">
                  <i className="fas fa-file-invoice me-2 text-primary"></i>Uploaded Documents & Certificates
                </h6>
                <Row className="g-3">
                  {/* NID Card */}
                  <Col md="4">
                    <Card className="border shadow-none h-100">
                      <CardBody className="text-center">
                        <div className="mb-3">
                          <i className="fas fa-id-card fa-3x text-primary"></i>
                        </div>
                        <h6 className="fw-bold mb-1">National ID Card (NID)</h6>
                        <p className="text-muted fs-7 mb-3">Identity Verification Document</p>
                        {userData.nid_card ? (
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => setZoomImage(userData.nid_card)}
                            >
                              <i className="fas fa-eye me-1"></i> View NID
                            </Button>
                            <a
                              href={userData.nid_card}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-outline-secondary"
                            >
                              <i className="fas fa-external-link-alt"></i>
                            </a>
                          </div>
                        ) : (
                          <Badge color="light" className="text-muted p-2">Not Uploaded</Badge>
                        )}
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Birth Certificate */}
                  <Col md="4">
                    <Card className="border shadow-none h-100">
                      <CardBody className="text-center">
                        <div className="mb-3">
                          <i className="fas fa-certificate fa-3x text-success"></i>
                        </div>
                        <h6 className="fw-bold mb-1">Birth Certificate</h6>
                        <p className="text-muted fs-7 mb-3">Age & Birth Record</p>
                        {userData.birth_certificate ? (
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => setZoomImage(userData.birth_certificate)}
                            >
                              <i className="fas fa-eye me-1"></i> View Certificate
                            </Button>
                            <a
                              href={userData.birth_certificate}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-outline-secondary"
                            >
                              <i className="fas fa-external-link-alt"></i>
                            </a>
                          </div>
                        ) : (
                          <Badge color="light" className="text-muted p-2">Not Uploaded</Badge>
                        )}
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Last Education Certificate */}
                  <Col md="4">
                    <Card className="border shadow-none h-100">
                      <CardBody className="text-center">
                        <div className="mb-3">
                          <i className="fas fa-graduation-cap fa-3x text-warning"></i>
                        </div>
                        <h6 className="fw-bold mb-1">Education Certificate</h6>
                        <p className="text-muted fs-7 mb-3">Last Academic Qualification</p>
                        {userData.last_education_certificate ? (
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              color="warning"
                              size="sm"
                              className="text-white"
                              onClick={() => setZoomImage(userData.last_education_certificate)}
                            >
                              <i className="fas fa-eye me-1"></i> View Certificate
                            </Button>
                            <a
                              href={userData.last_education_certificate}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-outline-secondary"
                            >
                              <i className="fas fa-external-link-alt"></i>
                            </a>
                          </div>
                        ) : (
                          <Badge color="light" className="text-muted p-2">Not Uploaded</Badge>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </TabPane>
            </TabContent>
          )}
        </ModalBody>

        <ModalFooter className="bg-light border-top">
          {onEditUser && (
            <Button
              color="primary"
              onClick={() => {
                toggle();
                onEditUser(userData);
              }}
            >
              <i className="fas fa-edit me-1"></i> Edit User Profile
            </Button>
          )}
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Document Image Zoom Modal */}
      {zoomImage && (
        <Modal isOpen={!!zoomImage} toggle={() => setZoomImage(null)} size="lg" centered>
          <ModalHeader toggle={() => setZoomImage(null)}>Document Preview</ModalHeader>
          <ModalBody className="text-center p-3">
            <img
              src={zoomImage}
              alt="Document Preview"
              className="img-fluid rounded shadow"
              style={{ maxHeight: "75vh", objectFit: "contain" }}
            />
          </ModalBody>
          <ModalFooter>
            <a href={zoomImage} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">
              <i className="fas fa-download me-1"></i> Open Full / Download
            </a>
            <Button color="secondary" size="sm" onClick={() => setZoomImage(null)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default QwikITUserDetailsModal;
